import Vue from 'vue'
import { encryptPasswordForAuth } from '@/utils/authCrypto'

const USERS_KEY = 'app_users'
const SESSION_KEY = 'app_session'

export const sessionState = Vue.observable({ user: null })

function useBackend() {
  return process.env.VUE_APP_USE_BACKEND === 'true'
}

function apiUrl(path) {
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return root ? `${root}${p}` : p
}

function readUsers() {
  try {
    const raw = window.localStorage.getItem(USERS_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

function writeUsers(users) {
  window.localStorage.setItem(USERS_KEY, JSON.stringify(users || {}))
}

export function isLoggedIn() {
  const s = sessionState.user
  if (!s || !s.username) return false
  if (useBackend()) return Boolean(s.token)
  return true
}

export function syncSession() {
  const s = getSession()
  if (s && s.username && useBackend() && !s.token) {
    window.localStorage.removeItem(SESSION_KEY)
    sessionState.user = null
    return
  }
  sessionState.user = s
}

/** 清除 session 并跳转登录页（忽略导航守卫重定向产生的 rejection） */
export function redirectToLogin(router, redirectPath) {
  logoutUser()
  const path = redirectPath || '/home'
  router.push({ name: 'login', query: { redirect: path } }).catch(() => {})
}

export async function refreshProfileFromServer() {
  if (!useBackend()) return
  const s = getSession()
  if (!s || !s.token) return
  try {
    const res = await fetch(apiUrl('/api/auth/me'), {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${s.token}`
      }
    })
    if (res.status === 401) {
      logoutUser()
      return
    }
    const data = await res.json().catch(() => ({}))
    if (data && data.ok && data.user) {
      const next = {
        username: data.user.username,
        loginAt: s.loginAt || Date.now(),
        token: s.token,
        balance: typeof data.user.balance === 'number' ? data.user.balance : Number(data.user.balance),
        userId: data.user.id,
        agentLevel: data.user.agentLevel || 'normal'
      }
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(next))
      syncSession()
    }
  } catch (e) {
    /* 静默失败，界面仍可用本地 session */
  }
}

export async function registerUser({ username, password }) {
  if (useBackend()) {
    try {
      const passwordEnc = await encryptPasswordForAuth(password)
      const res = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, passwordEnc })
      })
      const data = await res.json().catch(() => ({}))
      if (data && data.ok) return { ok: true }
      return { ok: false, message: (data && data.message) || 'REGISTER_FAILED' }
    } catch (e) {
      if (e && e.message === 'PUBLIC_KEY_UNAVAILABLE') {
        return { ok: false, message: 'NETWORK_ERROR' }
      }
      return { ok: false, message: 'NETWORK_ERROR' }
    }
  }

  const users = readUsers()
  if (users[username]) {
    return { ok: false, message: 'USER_EXISTS' }
  }
  users[username] = { username, password, createdAt: Date.now(), balance: 0 }
  writeUsers(users)
  return { ok: true }
}

export async function loginUser({ username, password }) {
  if (useBackend()) {
    try {
      const passwordEnc = await encryptPasswordForAuth(password)
      const res = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, passwordEnc })
      })
      const data = await res.json().catch(() => ({}))
      if (data && data.ok && data.token && data.user) {
        window.localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            username: data.user.username,
            loginAt: Date.now(),
            token: data.token,
            balance:
              typeof data.user.balance === 'number' ? data.user.balance : Number(data.user.balance || 0),
            userId: data.user.id,
            agentLevel: data.user.agentLevel || 'normal'
          })
        )
        syncSession()
        return { ok: true }
      }
      return { ok: false, message: (data && data.message) || 'LOGIN_FAILED' }
    } catch (e) {
      if (e && e.message === 'PUBLIC_KEY_UNAVAILABLE') {
        return { ok: false, message: 'NETWORK_ERROR' }
      }
      return { ok: false, message: 'NETWORK_ERROR' }
    }
  }

  const users = readUsers()
  const user = users[username]
  if (!user) return { ok: false, message: 'USER_NOT_FOUND' }
  if (user.password !== password) return { ok: false, message: 'INVALID_PASSWORD' }
  const bal = typeof user.balance === 'number' ? user.balance : Number(user.balance || 0)
  window.localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      username,
      loginAt: Date.now(),
      balance: bal
    })
  )
  syncSession()
  return { ok: true }
}

export function logoutUser() {
  window.localStorage.removeItem(SESSION_KEY)
  syncSession()
}

export function getSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function getAuthHeader() {
  const s = getSession()
  if (s && s.token) {
    return { Authorization: `Bearer ${s.token}` }
  }
  return {}
}
